import { useState } from 'react'
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom'
import { ThemeToggle } from '../../components/ui/ThemeToggle'
import { InboxBell } from '../../shared/InboxBell'
import { PanelNavIcon } from '../../shared/PanelNavIcons'
import { BrandLogo } from '../../shared/BrandLogo'
import { ibApi } from '../api'
import { useIbAuth } from '../AuthProvider'

const nav = [
  { to: '/ib', label: 'Dashboard', icon: 'dashboard', end: true },
  { to: '/ib/clients', label: 'Clients', icon: 'clients' },
  { to: '/ib/positions', label: 'Net Positions', icon: 'positions' },
  { to: '/ib/wallet', label: 'Wallet', icon: 'wallet' },
] as const

export function IbLayout() {
  const { isAuthenticated, logout, subadmin } = useIbAuth()
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)

  if (!isAuthenticated) {
    return <Navigate to="/ib/login" replace state={{ from: location.pathname }} />
  }

  return (
    <div className={`ap-shell ap-shell--ib ${drawerOpen ? 'is-drawer-open' : ''}`}>
      <a className="skip-link" href="#ib-main">
        Skip to content
      </a>

      <button
        type="button"
        className="ap-menu-btn"
        aria-expanded={drawerOpen}
        aria-controls="ib-drawer"
        onClick={() => setDrawerOpen((v) => !v)}
      >
        <span className="ap-menu-btn__bars" aria-hidden="true" />
        Menu
      </button>

      <div
        className="ap-drawer-backdrop"
        hidden={!drawerOpen}
        onClick={() => setDrawerOpen(false)}
      />

      <aside id="ib-drawer" className="ap-sidebar" aria-label="IB navigation">
        <div className="ap-sidebar__brand">
          <BrandLogo tone="light" />
          <span className="ap-sidebar__role">IB / Sub-admin</span>
        </div>
        <p className="ap-sidebar__section">Partner desk</p>
        <nav className="ap-sidebar__nav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : false}
              onClick={() => setDrawerOpen(false)}
              className={({ isActive }) => (isActive ? 'is-active' : undefined)}
            >
              <PanelNavIcon name={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="ap-sidebar__foot">
          {subadmin?.code ? <span className="ap-sidebar__code">{subadmin.code}</span> : null}
          <button type="button" className="pp-btn pp-btn--secondary pp-btn--sm" onClick={logout}>
            Logout
          </button>
        </div>
      </aside>

      <div className="ap-main-wrap">
        <header className="ap-topbar">
          <div>
            <p className="ap-topbar__eyebrow">Partner portal</p>
            <strong className="ap-topbar__title">
              {subadmin?.displayName || subadmin?.email || 'IB'}
              {subadmin?.code ? ` · ${subadmin.code}` : ''}
            </strong>
          </div>
          <div className="ap-topbar__actions">
            <InboxBell
              classPrefix="ap"
              load={() =>
                ibApi.notifications().then((r) => ({
                  unreadCount: r.unreadCount,
                  notifications: r.notifications,
                }))
              }
              markRead={(id) =>
                ibApi.notificationsRead(id).then((r) => ({ unreadCount: r.unreadCount }))
              }
            />
            <span className="ap-topbar__badge ap-topbar__badge--ib">Sub-admin</span>
            <ThemeToggle />
          </div>
        </header>
        <main id="ib-main" className="ap-main">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
